import { Button } from '@mui/base';
import {
    Box,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Table,
    TableRow,
    useTheme,
} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import AppPagination from 'components/AppPagination';
import Scrollbar from 'components/ScrollBar';
import {
    BodyTableCell,
    HeadTableCell,
    StyledTableRow,
} from 'components/table/StyledTable';
import LoadingScreen from 'components/LoadingScreen';
import columnShape from 'page-sections/invoice-list/columnShape';
import HeadingArea from 'page-sections/invoice-list/heading-area';
import SearchArea from 'page-sections/invoice-list/search-area';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useAsyncDebounce,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import axiosInstance from 'utils/axios';
import downloadFile from 'utils/downloadFile';
import formatCurrency from 'utils/formatCurrency';
import useBoolean from 'hooks/useBoolean';

const InvoiceList = () => {
    const [value, setValue] = useState('all');
    const [dataInvoice, setDataInvoice] = useState([]);
    const [groupStatus, setGroupStatus] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [meta, setMeta] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 1,
    });
    const [refreshKey, setRefreshKey] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [term, setTerm] = useState('');
    const [deleteInvoiceData, SetDeleteInvoiceData] = useState({
        id: '',
        fullName: '',
        totalPrice: 0,
    });
    const theme = useTheme();
    const columns = useMemo(() => columnShape, []);

    const isLoading = useBoolean();
    const isDownloading = useBoolean();
    const openDialog = useBoolean();

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
    } = useTable(
        { columns, data: dataInvoice },
        useSortBy,
        useRowSelect,
    );

    const downloadInvoice = async (data) => {
        try {
            isLoading.onTrue();
            await downloadFile(`/api/prv/member-invoices/download/${data.id}`);
        } finally {
            isLoading.onFalse();
        }
    };

    const deleteInvoice = async (data) => {
        SetDeleteInvoiceData({
            fullName: data.user.fullName,
            id: data.id,
            totalPrice: data.totalPrice,
        });
        openDialog.onTrue();
    };

    const navigate = useNavigate();

    const handleClose = () => {
        openDialog.onFalse();
    };

    const handleYes = async () => {
        await axiosInstance
            .delete(`api/prv/invoices/${deleteInvoiceData.id}`)
            .then(() => {
                setRefreshKey((v) => v + 1);
                openDialog.onFalse();
            })
            .catch((err) => {
                console.log(err);
                openDialog.onFalse();
            });
    };

    useEffect(() => {
        const getData = async () => {
            try {
                isLoading.onTrue();
                const res = await axiosInstance.get(`/api/prv/invoices`, {
                    params: {
                        status: value,
                        packageTypes: ['main', 'additional'],
                        page: String(page),
                        limit: String(limit),
                        term: term || undefined,
                    },
                });

                const invoices = res.data?.data?.invoices ?? [];
                const summary = res.data?.data?.summary ?? [];
                const responseMeta = res.data?.meta ?? null;

                setDataInvoice(invoices);
                setGroupStatus(summary);
                if (responseMeta) setMeta(responseMeta);
            } catch (err) {
                console.log(err);
            } finally {
                isLoading.onFalse();
            }
        };

        getData();
    }, [limit, page, refreshKey, term, value]);

    const handleEdit = (data) => {
        navigate(`/dashboard/admin/invoice/${data.id}`);
    };

    const handleChange = (_, currentPageNo) => setPage(currentPageNo);
    const changeTab = (_, newValue) => {
        setValue(newValue);
        setPage(1);
    };
    const handleSearch = useAsyncDebounce((value) => {
        setTerm(value || '');
        setPage(1);
    }, 200);

    return (
        <Box pt={2} pb={4}>
            {(isLoading.value || isDownloading.value) && (
                <LoadingScreen showSpinner={true} />
            )}
            <Card sx={{ py: 2 }}>
                <Dialog
                    open={openDialog.value}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">
                        Are you sure to delete this data?
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Invoice atas nama {deleteInvoiceData.fullName},
                            Nominal{' '}
                            {formatCurrency(deleteInvoiceData.totalPrice)}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button onClick={handleYes} autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
                <Box px={3}>
                    <HeadingArea
                        value={value}
                        changeTab={changeTab}
                        header={groupStatus}
                    />
                    <SearchArea
                        value={searchValue}
                        onChange={handleSearch}
                        setValue={setSearchValue}
                        status={value}
                        isDownloading={isDownloading.value}
                        onDownloadStart={isDownloading.onTrue}
                        onDownloadEnd={isDownloading.onFalse}
                    />
                </Box>

                <Scrollbar autoHide={true}>
                    <Table
                        {...getTableProps()}
                        sx={{
                            minWidth: 900,
                        }}
                    >
                        <TableHead
                            sx={{
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {headerGroups.map((headerGroup, index) => (
                                <TableRow
                                    key={'header-row' + index}
                                    {...headerGroup.getHeaderGroupProps()}
                                >
                                    {headerGroup.headers.map(
                                        (column, indexCell) => (
                                            <HeadTableCell
                                                key={'header-cell' + indexCell}
                                                {...column.getHeaderProps(
                                                    column.getSortByToggleProps(),
                                                )}
                                                sx={{
                                                    '&:last-child': {
                                                        textAlign: 'center',
                                                    },
                                                }}
                                            >
                                                {column.render('Header')}
                                            </HeadTableCell>
                                        ),
                                    )}
                                </TableRow>
                            ))}
                        </TableHead>

                        <TableBody {...getTableBodyProps()}>
                            {rows.map((row, indexRow) => {
                                prepareRow(row);
                                return (
                                    <StyledTableRow
                                        {...row.getRowProps()}
                                        key={'row' + indexRow}
                                    >
                                        {row.cells.map((cell, indexCell) => {
                                            return (
                                                <BodyTableCell
                                                    key={'column' + indexCell}
                                                    {...cell.getCellProps()}
                                                >
                                                    {cell.render('Cell', {
                                                        handleDownload:
                                                            downloadInvoice,
                                                        handleDelete:
                                                            deleteInvoice,
                                                        handleEdit,
                                                        isLoading:
                                                            isLoading.value,
                                                    })}
                                                </BodyTableCell>
                                            );
                                        })}
                                    </StyledTableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Scrollbar>

                <Stack alignItems="center" marginY={4}>
                    <AppPagination
                        shape="rounded"
                        onChange={handleChange}
                        page={page}
                        count={meta.totalPages || 1}
                    />
                </Stack>
            </Card>
        </Box>
    );
};

export default InvoiceList;
